import classNames from 'classnames';
import React, { forwardRef, Ref, useId } from 'react';
import InlineError from '../InlineError';
import Tooltip from '../Tooltip';
import ToggleButton from './components/ToggleButton/ToggleButton';
import './ToggleButtons.scss';

interface RadioOption {
  label: string;
  value: string | number | boolean;
}

interface ToggleButtonsProps {
  label: React.ReactNode | React.ReactNode[];
  hideLabel?: boolean;
  required?: boolean;
  options: RadioOption[] | string[];
  name: string;
  value?: string | number | boolean;
  onChange?: (value: string | number | boolean) => void;
  onBlur?: () => void;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  error?: string;
  pii?: boolean;
  horizontal?: boolean;
}

const ToggleButtons = forwardRef<HTMLInputElement, ToggleButtonsProps>(
  (
    {
      label,
      hideLabel = false,
      required = false,
      pii = false,
      options,
      name,
      value = [],
      onChange,
      onBlur,
      tooltip,
      tooltipAriaLabel,
      error
    },
    ref
  ) => {
    const id = useId();

    const radios = options.map((option, index) => (
      <ToggleButton
        key={`${name}-${index}`}
        label={typeof option === 'string' ? option : option.label}
        name={name}
        onChange={() => {
          onChange(typeof option === 'string' ? option : option.value);
        }}
        onBlur={onBlur}
        checked={typeof option === 'string' ? value === option : value === option.value}
        error={!!error}
        ref={index === 0 ? ref : null}
      />
    ));

    return (
      <fieldset
        className={classNames('mss-form-control mss-toggle-buttons', {
          'ctHidden pii-data': pii
        })}
        aria-describedby={error ? `error-${id}` : undefined}
        aria-labelledby={`toggle-buttons-label-${id}`}
        aria-invalid={!!error}
      >
        <div
          id={`toggle-buttons-label-${id}`}
          className={classNames('mss-form-control-label', {
            'sr-only': hideLabel
          })}
        >
          {label}
          {required && (
            <span className="mss-form-control-required" aria-hidden>
              *
            </span>
          )}
          {tooltip && (
            <Tooltip content={tooltip} buttonProps={{ 'aria-label': tooltipAriaLabel }} />
          )}
        </div>
        <div className={classNames('mss-toggle-buttons__wrapper')}>{radios}</div>
        <div role="alert">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {((error as any) instanceof String || typeof error === 'string') && (
            <InlineError error={error as string} id={id} />
          )}
        </div>
      </fieldset>
    );
  }
);

export default ToggleButtons as (
  props: ToggleButtonsProps & { ref?: Ref<HTMLInputElement> }
) => JSX.Element;
